import React, { useState, useEffect, useRef } from 'react';
import MapFrame from '../../components/MapFrame';
import BlockSubtitle from '../../components/BlockSubtitle';
import MusicPlayer from '../../components/MusicPlayer';
import './Ivory.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const src = u => !u ? null : u.startsWith('http') ? u : `${u}`;
const fmtDate = raw => {
  if (!raw) return 'ДД · ММ · ГГГГ';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join(' · ');
};

const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

/* ── useReveal ───────────────────────────────────────────────── */
function useReveal(pageRef, blockKey) {
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const SEL = '.ivo-rise, .ivo-fade, .ivo-line-anim, .ivo-expand';
    const inPreview = !!root.closest('.lp-template-inner, .lp-template-outer');
    if (inPreview) {
      root.querySelectorAll(SEL).forEach(el => el.classList.add('ivo-in'));
      return;
    }
    const els = root.querySelectorAll(SEL);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('ivo-in'); obs.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [pageRef, blockKey]);
}

/* ── Countdown ───────────────────────────────────────────────── */
function Countdown({ date, accent }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const ms = Math.max(0, new Date(date) - Date.now());
      setT({ d: Math.floor(ms / 86400000), h: Math.floor(ms % 86400000 / 3600000),
             m: Math.floor(ms % 3600000 / 60000), s: Math.floor(ms % 60000 / 1000) });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [date]);
  return (
    <div className="ivo-countdown">
      {[['дней', t.d], ['часов', t.h], ['минут', t.m], ['секунд', t.s]].map(([u, v]) => (
        <div key={u} className="ivo-cu">
          <span className="ivo-cu-val" style={{ color: accent }}>{String(v).padStart(2, '0')}</span>
          <span className="ivo-cu-label">{u}</span>
        </div>
      ))}
    </div>
  );
}

/* ── CalendarWidget ──────────────────────────────────────────── */
function CalendarWidget({ date, accent, addBtn }) {
  if (!date) return null;
  const d = new Date(date), yr = d.getFullYear(), mo = d.getMonth(), day = d.getDate();
  const off = (new Date(yr, mo, 1).getDay() + 6) % 7;
  const dim = new Date(yr, mo + 1, 0).getDate();
  const prevDim = new Date(yr, mo, 0).getDate();
  const cells = [];
  for (let i = off - 1; i >= 0; i--) cells.push({ n: prevDim - i, cur: false });
  for (let i = 1; i <= dim; i++) cells.push({ n: i, cur: true });
  while (cells.length < 42) cells.push({ n: cells.length - off - dim + 1, cur: false });
  const MON = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
  return (
    <div className="ivo-calendar">
      <div className="ivo-cal-header">
        <span style={{ color: accent }}>{MON[mo]}</span>
        <span className="ivo-cal-year">{yr}</span>
      </div>
      <div className="ivo-cal-grid">
        {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map(h => (
          <div key={h} className="ivo-cal-head">{h}</div>
        ))}
        {cells.map((c, i) => {
          const isWedding = c.cur && c.n === day;
          return (
            <div key={i}
              className={`ivo-cal-cell${!c.cur ? ' ivo-cal-muted' : ''}${isWedding ? ' ivo-cal-today' : ''}`}
              style={isWedding ? { background: accent, color: '#fff' } : {}}
            >{c.n}</div>
          );
        })}
      </div>
      {addBtn}
    </div>
  );
}

/* ── RSVPForm ────────────────────────────────────────────────── */
function RSVPForm({ project, accent }) {
  const [status,  setStatus]  = useState('confirmed');
  const [name,    setName]    = useState('');
  const [survey,  setSurvey]  = useState({});
  const [message, setMessage] = useState('');
  const [done,    setDone]    = useState(false);
  const bc = project.block_content || {};

  if (done) return (
    <div className="ivo-rsvp-done" style={{ borderColor: accent }}>
      <div className="ivo-done-mark" style={{ color: accent }}>✓</div>
      <strong style={{ color: accent }}>Благодарим вас</strong>
      <p>{status === 'confirmed' ? 'Мы рады, что вы будете с нами.' : 'Жаль, что вы не сможете присутствовать.'}</p>
    </div>
  );

  return (
    <form className="ivo-rsvp-form" onSubmit={e => { e.preventDefault(); setDone(true); }}>
      <div className="ivo-rsvp-options">
        {[{ v: 'confirmed', l: bc.rsvp?.yesText || 'Буду присутствовать' }, { v: 'declined', l: bc.rsvp?.noText || 'К сожалению, не смогу' }].map(o => (
          <label key={o.v}
            className={`ivo-rsvp-opt${status === o.v ? ' active' : ''}`}
            style={status === o.v ? { borderColor: accent, color: accent } : {}}
          >
            <input type="radio" name="rsvp" value={o.v} checked={status === o.v}
              onChange={() => setStatus(o.v)} style={{ display: 'none' }} />
            {o.l}
          </label>
        ))}
      </div>

      <input className="ivo-input" placeholder="Ваше имя"
        value={name} onChange={e => setName(e.target.value)} required />

      {project.blocks?.includes('survey') && status === 'confirmed' && project.survey?.map(q => (
        <div key={q.id} className="ivo-survey-q">
          <p className="ivo-survey-label">{q.question}</p>
          {q.type === 'text'
            ? <textarea className="ivo-input" rows={2} value={survey[q.id] || ''}
                onChange={e => setSurvey(s => ({ ...s, [q.id]: e.target.value }))} />
            : q.type === 'multiple'
            ? <div className="ivo-survey-opts">{(q.options || []).map(opt => {
                const selected = Array.isArray(survey[q.id]) ? survey[q.id].includes(opt) : false;
                return (
                  <label key={opt}
                    className={`ivo-rsvp-opt ivo-rsvp-opt-sm${selected ? ' active' : ''}`}
                    style={selected ? { borderColor: accent, color: accent } : {}}
                  >
                    <input type="checkbox" checked={selected} onChange={e => {
                      const cur = Array.isArray(survey[q.id]) ? survey[q.id] : [];
                      setSurvey(s => ({ ...s, [q.id]: e.target.checked ? [...cur, opt] : cur.filter(v => v !== opt) }));
                    }} style={{ display: 'none' }} />
                    {opt}
                  </label>
                );
              })}</div>
            : <div className="ivo-survey-opts">{(q.options || []).map(opt => (
                <label key={opt}
                  className={`ivo-rsvp-opt ivo-rsvp-opt-sm${survey[q.id] === opt ? ' active' : ''}`}
                  style={survey[q.id] === opt ? { borderColor: accent, color: accent } : {}}
                >
                  <input type="radio" name={q.id} value={opt}
                    onChange={() => setSurvey(s => ({ ...s, [q.id]: opt }))} style={{ display: 'none' }} />
                  {opt}
                </label>
              ))}</div>
          }
        </div>
      ))}

      {project.blocks?.includes('message') && (
        <div>
          {bc.message?.fieldLabel && (
            <p style={{ fontSize: '0.8rem', color: '#888880', marginBottom: 4 }}>{bc.message.fieldLabel}</p>
          )}
          <textarea className="ivo-input" rows={3}
            placeholder={bc.message?.text || 'Ваши пожелания...'}
            value={message} onChange={e => setMessage(e.target.value)} />
        </div>
      )}
      <button type="submit" className="ivo-btn" style={{ borderColor: accent, color: accent }}>
        {bc.rsvp?.btnText || 'Подтвердить участие'}
      </button>
    </form>
  );
}

/* ── Wishlist ────────────────────────────────────────────────── */
function Wishlist({ project, accent }) {
  const [reserved, setReserved] = useState({});
  const items = project.wishlist || [];
  if (!items.length) return null;
  return (
    <div className="ivo-wishlist">
      {items.map(item => (
        <div key={item.id} className="ivo-wish-item">
          <div className="ivo-wish-info">
            <div className="ivo-wish-name">{item.name}</div>
            {item.price && <div className="ivo-wish-price" style={{ color: accent }}>{item.price} ₽</div>}
          </div>
          <div className="ivo-wish-actions">
            {item.link && (
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="ivo-btn-text"
                 style={{ color: accent }}>Посмотреть →</a>
            )}
            {reserved[item.id]
              ? <button type="button" className="ivo-btn ivo-btn-muted"
                  onClick={() => setReserved(r => ({ ...r, [item.id]: false }))}>
                  ✓ Снять резерв
                </button>
              : <button type="button" className="ivo-btn" style={{ borderColor: accent, color: accent }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: true }))}>
                  Зарезервировать
                </button>
            }
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Gold Rule ───────────────────────────────────────────────── */
function GoldRule({ accent, className = '' }) {
  return <div className={`ivo-rule ivo-line-anim ${className}`} style={{ background: accent }} />;
}

/* ── Section Header ─────────────────────────────────────────── */
function SectionHeader({ title, subtitle, accent }) {
  return (
    <div className="ivo-section-header">
      <GoldRule accent={accent} />
      <h2 className="ivo-section-title ivo-expand">{title}</h2>
      <BlockSubtitle text={subtitle} className="ivo-section-subtitle ivo-fade" />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN — Ivory
══════════════════════════════════════════════════════════════ */
export default function Ivory({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blocks  = project.blocks || [];
  useReveal(pageRef, blocks.join(','));

  const bc     = project.block_content || {};
  const accent = project.palette?.primary || '#b08d57';

  const fontTitle = project.fonts?.title || 'EB Garamond';
  const fontBody  = project.fonts?.body  || 'Lato';
  const fontStyle = {
    '--f-title':  `'${fontTitle}', serif`,
    '--f-body':   `'${fontBody}', sans-serif`,
    '--c-accent': accent,
  };

  /* Dynamic Google Fonts load */
  useEffect(() => {
    const id = 'ivo-dynamic-fonts';
    let link = document.getElementById(id);
    if (!link) {
      link = document.createElement('link');
      link.id = id; link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    const families = [fontTitle, fontBody].filter(Boolean);
    const query = families.map(f => `family=${encodeURIComponent(f)}:wght@300;400;500;600;700`).join('&');
    link.href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  }, [fontTitle, fontBody]);

  /* Photos */
  const photos    = project.cover_photos || (project.cover_photo ? [{ url: project.cover_photo }] : []);
  const invPhotos = project.invitation_photos || [];
  const invP1     = invPhotos[0] || photos[0] || null;
  const invP2     = invPhotos[1] || photos[1] || null;
  const dcPhotos  = project.dresscode_photos || [];

  const bride = project.bride || 'Невеста';
  const groom = project.groom || 'Жених';
  const venues = (project.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());

  const tomorrowISO = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
  const weddingDate = project.wedding_date || tomorrowISO;

  const HAS_MENU      = blocks.includes('menu');
  const HAS_CALENDAR  = blocks.includes('calendar');
  const HAS_COUNTDOWN = blocks.includes('countdown');
  const HAS_SCHEDULE  = blocks.includes('schedule');
  const HAS_MAP = blocks.includes('map') && ((p?.venues || project?.venues || []).some(v => v?.address?.trim()) || !!(p?.wedding_address || project?.wedding_address));
  const HAS_DRESSCODE = blocks.includes('palette');
  const HAS_RSVP      = blocks.includes('rsvp');
  const HAS_WISHLIST  = blocks.includes('wishlist');

  const allItems  = project.schedule || [];
  const optional  = allItems.filter(i => i.optional);
  const mandatory = allItems.filter(i => !i.optional);
  const mainItems = optional.length > 0 ? mandatory : allItems;

  /* Google Calendar link */
  const calLink = (() => {
    const d = new Date(weddingDate);
    const pad = n => String(n).padStart(2, '0');
    const ymd = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
    const title = encodeURIComponent(`Свадьба ${bride} & ${groom}`);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${ymd}/${ymd}`;
  })();

  return (
    <div className="ivo-page" ref={pageRef} style={fontStyle}
      data-template="Ivory" container-type="inline-size" container-name="ivo-root">

      {/* ══ NAV ═══════════════════════════════════════════════════ */}
      {HAS_MENU && (
        <nav className="ivo-nav">
          <div className="ivo-nav-inner">
            <span className="ivo-nav-brand">{bride} &amp; {groom}</span>
            <div className="ivo-nav-rule" style={{ background: accent }} />
            <div className="ivo-nav-links">
              {HAS_SCHEDULE  && <a href="#schedule">Тайминг</a>}
              {HAS_MAP       && <a href="#map">Локация</a>}
              {HAS_DRESSCODE && <a href="#dresscode">Дресс-код</a>}
              {HAS_RSVP      && <a href="#rsvp">Анкета</a>}
            </div>
          </div>
        </nav>
      )}

      {/* ══ HERO ══════════════════════════════════════════════════ */}
      <section className="ivo-hero">
        {/* Left — Typography */}
        <div className="ivo-hero-left">
          <div className="ivo-hero-eyebrow ivo-fade">
            {project.wedding_slogan || bc.hero?.subtitle || 'Приглашение на свадьбу'}
          </div>

          <div className="ivo-hero-rule-wrap ivo-line-anim">
            <div className="ivo-hero-rule" style={{ background: accent }} />
          </div>

          <h1 className="ivo-hero-names ivo-rise">
            <span className="ivo-hero-bride">{bride}</span>
            <span className="ivo-hero-amp" style={{ color: accent }}>&amp;</span>
            <span className="ivo-hero-groom">{groom}</span>
          </h1>

          <div className="ivo-hero-rule-wrap ivo-line-anim" style={{ '--d': '.2s' }}>
            <div className="ivo-hero-rule" style={{ background: accent }} />
          </div>

          <div className="ivo-hero-meta ivo-fade" style={{ '--d': '.35s' }}>
            <p className="ivo-hero-date">{fmtDate(weddingDate)}</p>
            {project.wedding_time && (
              <p className="ivo-hero-time">{project.wedding_time}</p>
            )}
            {venues.length > 0 && venues[0].name && (
              <p className="ivo-hero-venue">{venues[0].name}</p>
            )}
          </div>

          {HAS_RSVP && (
            <a href="#rsvp" className="ivo-btn ivo-hero-btn ivo-rise" style={{ '--d': '.5s', borderColor: accent, color: accent }}>
              {bc.rsvp?.yesText || 'Подтвердить присутствие'}
            </a>
          )}
        </div>

        {/* Right — Photo */}
        <div className="ivo-hero-right ivo-fade" style={{ '--d': '.25s' }}>
          {photos[0] ? (
            <div className="ivo-hero-photo">
              <img src={src(photos[0].url)} alt={`${bride} & ${groom}`} style={{ filter: flt(photos[0]), objectPosition: objPos(photos[0]) }} />
            </div>
          ) : (
            <div className="ivo-hero-photo ivo-hero-photo-empty">
              <span>Ваше фото</span>
            </div>
          )}
        </div>
      </section>

      {/* ══ INVITATION ════════════════════════════════════════════ */}
      <section className="ivo-section ivo-invitation" id="invitation">
        <div className="ivo-container">
          <SectionHeader
            title={bc.message?.title || 'Дорогие гости'}
            subtitle={null}
            accent={accent}
          />
          <div className="ivo-invitation-body ivo-rise">
            {bc.message?.text
              ? <div className="ivo-prose" dangerouslySetInnerHTML={{ __html: bc.message.text }} />
              : (
                <div className="ivo-prose">
                  <p>С искренней радостью и глубокой нежностью мы приглашаем вас разделить с нами торжество нашего бракосочетания.</p>
                  <p>В этот день, наполненный любовью и благодарностью, мы будем счастливы видеть рядом тех, кто нам дорог.</p>
                  <p>Пожалуйста, подтвердите своё присутствие заранее, чтобы мы могли должным образом подготовиться.</p>
                </div>
              )
            }
          </div>

          {/* Two photos side by side */}
          {(invP1 || invP2) && (
            <div className="ivo-inv-photos">
              {invP1 && (
                <div className="ivo-inv-photo ivo-rise" style={{ '--d': '.15s' }}>
                  <img src={src(invP1.url)} alt="" style={{ filter: flt(invP1), objectPosition: objPos(invP1) }} />
                </div>
              )}
              {invP2 && (
                <div className="ivo-inv-photo ivo-rise" style={{ '--d': '.25s' }}>
                  <img src={src(invP2.url)} alt="" style={{ filter: flt(invP2), objectPosition: objPos(invP2) }} />
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ══ CALENDAR + COUNTDOWN ══════════════════════════════════ */}
      {(HAS_CALENDAR || HAS_COUNTDOWN) && (
        <section className="ivo-section ivo-cal-section ivo-warm-bg" id="calendar">
          <div className="ivo-container">
            {HAS_CALENDAR && (
              <div className="ivo-cal-wrap">
                <SectionHeader
                  title={bc.calendar?.title || 'Дата свадьбы'}
                  subtitle={null}
                  accent={accent}
                />
                <div className="ivo-rise" style={{ '--d': '.1s' }}>
                  <CalendarWidget
                    date={weddingDate}
                    accent={accent}
                    addBtn={
                      <a href={calLink} target="_blank" rel="noopener noreferrer"
                         className="ivo-btn ivo-cal-add-btn" style={{ borderColor: accent, color: accent }}>
                        Добавить в календарь
                      </a>
                    }
                  />
                </div>
              </div>
            )}
            {HAS_COUNTDOWN && (
              <div className="ivo-countdown-wrap ivo-rise" style={{ '--d': '.15s' }}>
                <SectionHeader
                  title={bc.countdown?.title || 'До торжества'}
                  subtitle={bc.countdown?.subtitle || null}
                  accent={accent}
                />
                <Countdown date={weddingDate} accent={accent} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══ SCHEDULE ══════════════════════════════════════════════ */}
      {HAS_SCHEDULE && (
        <section className="ivo-section ivo-schedule-section" id="schedule">
          <div className="ivo-container">
            <SectionHeader
              title={bc.schedule?.title || 'Программа вечера'}
              subtitle={bc.schedule?.subtitle || null}
              accent={accent}
            />
            <div className="ivo-timeline">
              {allItems.length > 0 ? (
                <>
                  {optional.length > 0 && (
                    <div className="ivo-tl-group">
                      <div className="ivo-tl-group-label">Необязательная часть</div>
                      {optional.map((item, i) => (
                        <div key={i} className="ivo-tl-item ivo-rise" style={{ '--d': `${i * 0.1}s` }}>
                          <div className="ivo-tl-marker" style={{ background: accent }} />
                          <div className="ivo-tl-line" style={{ background: accent }} />
                          <div className="ivo-tl-time" style={{ color: accent }}>{item.time}</div>
                          <div className="ivo-tl-info">
                            <div className="ivo-tl-event">{item.event}</div>
                            {item.description && <div className="ivo-tl-desc">{item.description}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="ivo-tl-group">
                    {optional.length > 0 && <div className="ivo-tl-group-label">Основная программа</div>}
                    {mainItems.map((item, i) => (
                      <div key={i} className="ivo-tl-item ivo-rise" style={{ '--d': `${i * 0.1}s` }}>
                        <div className="ivo-tl-marker" style={{ background: accent }} />
                        <div className="ivo-tl-line" style={{ background: accent }} />
                        <div className="ivo-tl-time" style={{ color: accent }}>{item.time}</div>
                        <div className="ivo-tl-info">
                          <div className="ivo-tl-event">{item.event}</div>
                          {item.description && <div className="ivo-tl-desc">{item.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                [['14:00','Сбор гостей'],['15:00','Церемония'],['16:30','Фотосессия'],['18:00','Торжественный ужин']].map(([t, e], i) => (
                  <div key={i} className="ivo-tl-item" style={{ opacity: 0.4 }}>
                    <div className="ivo-tl-marker" style={{ background: '#ccc' }} />
                    <div className="ivo-tl-time" style={{ color: '#ccc' }}>{t}</div>
                    <div className="ivo-tl-event">{e}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {/* ══ MAP ═══════════════════════════════════════════════════ */}
      {HAS_MAP && (
        <section className="ivo-section ivo-map-section ivo-warm-bg" id="map">
          <div className="ivo-container">
            <SectionHeader
              title={bc.map?.title || 'Место проведения'}
              subtitle={bc.map?.subtitle || null}
              accent={accent}
            />
            <div className="ivo-venues ivo-rise">
              {venues.length > 0 ? venues.map((v, i) => (
                <div key={i} className="ivo-venue-row">
                  <div className="ivo-venue-num" style={{ color: accent }}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="ivo-venue-info">
                    {v.label && <span className="ivo-venue-label" style={{ color: accent }}>{v.label}</span>}
                    {v.name  && <span className="ivo-venue-name">{v.name}</span>}
                    <span className="ivo-venue-addr">{v.address}</span>
                  </div>
                  <a href={`https://yandex.ru/maps/?text=${encodeURIComponent(v.address)}&z=16`}
                     target="_blank" rel="noopener noreferrer"
                     className="ivo-btn-text" style={{ color: accent }}>
                    Маршрут →
                  </a>
                </div>
              )) : (
                <div style={{ color: '#888880', fontSize: '0.9rem', padding: '8px 0' }}>
                  {project.wedding_address || 'Адрес будет добавлен позже'}
                </div>
              )}
            </div>
            {(project.map_url || (project.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
            <div className="ivo-map-frame ivo-rise" style={{ '--d': '.2s' }}>
              {(() => {
                if (project.map_url) {
                  return <MapFrame region={project.region} title="Карта" src={project.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                }
                const withCoords = venues.filter(v => v.lat && v.lon);
                if (withCoords.length > 0) {
                  const clat = withCoords.reduce((s, v) => s + v.lat, 0) / withCoords.length;
                  const clon = withCoords.reduce((s, v) => s + v.lon, 0) / withCoords.length;
                  const pts  = withCoords.map(v => `${v.lon},${v.lat},pm2rdm`).join('~');
                  const zoom = withCoords.length > 1 ? 12 : 16;
                  return <MapFrame region={project.region} title="Карта" src={`https://yandex.ru/map-widget/v1/?ll=${clon},${clat}&z=${zoom}&pt=${pts}&lang=ru_RU`} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                }
                const addr = venues.length > 0
                  ? venues.map(v => v.address).filter(Boolean).join(' — ')
                  : (project.wedding_address || '');
                if (!addr) {
                  return <MapFrame region={project.region} title="Карта" src="https://yandex.ru/map-widget/v1/?ll=37.617617,55.755811&z=12&lang=ru_RU" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                }
                const mapSrc = venues.length > 1
                  ? `https://yandex.ru/map-widget/v1/?rtext=${venues.map(v => encodeURIComponent(v.address)).join('~')}&rtt=auto&lang=ru_RU`
                  : `https://yandex.ru/map-widget/v1/?text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                return <MapFrame region={project.region} title="Карта" src={mapSrc} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
              })()}
            </div>
            )}
          </div>
        </section>
      )}

      {/* ══ DRESSCODE ═════════════════════════════════════════════ */}
      {HAS_DRESSCODE && (
        <section className="ivo-section ivo-dresscode-section" id="dresscode">
          <div className="ivo-container">
            <SectionHeader
              title={bc.palette?.title || 'Дресс-код'}
              subtitle={bc.palette?.subtitle || null}
              accent={accent}
            />
            <div className="ivo-swatches ivo-rise">
              {(project.dresscode_palette || []).map((sw, i) => {
                const c = sw.color || '#fff';
                const hex = c.replace('#', '');
                const r = parseInt(hex.slice(0, 2), 16) || 255;
                const g = parseInt(hex.slice(2, 4), 16) || 255;
                const b = parseInt(hex.slice(4, 6), 16) || 255;
                const isLight = (r * 299 + g * 587 + b * 114) / 1000 > 160;
                return (
                  <div key={i} className="ivo-swatch"
                    style={{ background: c, border: isLight ? '1px solid #ddd' : 'none' }}>
                    <span style={{ color: isLight ? '#1c1c1c' : '#fff' }}>{sw.label}</span>
                  </div>
                );
              })}
            </div>
            {dcPhotos.length > 0 && (
              <div className="ivo-dc-photos">
                {dcPhotos.map((p, i) => (
                  <div key={i} className="ivo-dc-photo ivo-rise" style={{ '--d': `${i * 0.12}s` }}>
                    <img src={src(p.url)} alt="" style={{ filter: flt(p), objectPosition: objPos(p) }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══ RSVP + WISHLIST ═══════════════════════════════════════ */}
      {(HAS_RSVP || HAS_WISHLIST) && (
        <section className="ivo-section ivo-rsvp-section ivo-warm-bg" id="rsvp">
          <div className="ivo-container">
            {HAS_RSVP && (
              <div className="ivo-rsvp-block ivo-rise">
                <SectionHeader
                  title={bc.rsvp?.title || 'Подтверждение'}
                  subtitle={bc.rsvp?.subtitle || 'Пожалуйста, подтвердите ваше присутствие.'}
                  accent={accent}
                />
                {bc.rsvp?.deadline && (
                  <p className="ivo-deadline" style={{ color: accent }}>
                    Срок подтверждения: <strong>{new Date(bc.rsvp.deadline).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                  </p>
                )}
                <RSVPForm project={project} accent={accent} />
              </div>
            )}
            {HAS_WISHLIST && (
              <div className="ivo-wishlist-block ivo-rise" style={{ '--d': '.15s' }}>
                <SectionHeader
                  title={bc.wishlist?.title || 'Список желаний'}
                  subtitle={bc.wishlist?.subtitle || 'Мы составили список — он поможет вам с выбором.'}
                  accent={accent}
                />
                <Wishlist project={project} accent={accent} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══ FOOTER ════════════════════════════════════════════════ */}
      <footer className="ivo-footer">
        <div className="ivo-footer-inner">
          <div className="ivo-footer-rule" style={{ background: accent }} />
          <p className="ivo-footer-names">{bride} &amp; {groom}</p>
          <p className="ivo-footer-date">{fmtDate(weddingDate)}</p>
          <div className="ivo-footer-rule ivo-footer-rule-thin" style={{ background: accent }} />
        </div>
      </footer>

      {/* ══ WATERMARK (free plan) ═════════════════════════════════ */}
      {project.payment_plan === 'free' && (
        <div className="ivo-watermark">
          Сделано на <strong>WeddingApp</strong>
        </div>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={isPreview} />
      )}
    </div>
  );
}
